import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../components/common/Toast';
import { readTheme, type Theme } from '../../components/studio/studioUi';
import type { VideoRender } from '../../services/video.service';
import { socialService, type Placement, type PostOptions, type SocialAccount, type SocialConfig, type TiktokCreator } from '../../services/social.service';
import {
    BRAND, Check, CheckBox, DateField, Drawer, Ico, PLATFORM_NAME, PlatformIcon, Radio, Spin, TIKTOK_PRIVACY, TZ_NAME, TimeField,
    aspectLabel, ddButton, fieldLabel, fmtMB, fmtT, hhmm, inputStyle, isoDay, joinLocal, pillBtn, textareaStyle, vsError,
} from './vsUi';

export interface PostDrawerProps {
    onClose: () => void;
    videoName: string;
    renders: VideoRender[];
    /** Version to preselect; default: the newest finished render. */
    initialRenderId?: string;
    renderCost: number;
    /** Queue a 9:16 render of this video (charged like any render). */
    onRenderPortrait: () => void;
    onScheduled: () => void;
    theme?: Theme;
}

const CAPTION_MAX = 2200;
const TIKTOK_ORDER = ['PUBLIC_TO_EVERYONE', 'MUTUAL_FOLLOW_FRIENDS', 'FOLLOWER_OF_CREATOR', 'SELF_ONLY'];

/**
 * "Post to social" drawer (design "HyVay Studio - video"): 1 Choose version ·
 * 2 Channel & caption · 3 Schedule, with the chosen render playing on the side.
 * Business rules carried over from the old ScheduleDialog: TikTok reads the
 * creator's options right before posting and has no default privacy (Only me
 * until our app passes the audit), Story / Reel limits, 9:16-only platforms with
 * a "render a 9:16 copy" path, and the server's max days ahead.
 */
export const PostDrawer: React.FC<PostDrawerProps> = ({ onClose, videoName, renders, initialRenderId, renderCost, onRenderPortrait, onScheduled, theme }) => {
    const navigate = useNavigate();
    const toast = useToast();
    const done = useMemo(() => renders.filter((r) => r.status === 'DONE' && r.url), [renders]);
    const [step, setStep] = useState<1 | 2 | 3>(1);
    const [cfg, setCfg] = useState<SocialConfig | null>(null);
    const [accounts, setAccounts] = useState<SocialAccount[] | null>(null);
    const [renderId, setRenderId] = useState(initialRenderId || '');
    const [picked, setPicked] = useState<string[]>([]);
    const [openAcct, setOpenAcct] = useState<Record<string, boolean>>({});
    const [caption, setCaption] = useState(videoName);
    const [options, setOptions] = useState<Record<string, PostOptions>>({});
    const [creators, setCreators] = useState<Record<string, TiktokCreator | 'error'>>({});
    const [when, setWhen] = useState<'now' | 'later'>('now');
    const [day, setDay] = useState(() => isoDay(nextHour()));
    const [time, setTime] = useState(() => hhmm(nextHour()));
    const [busy, setBusy] = useState(false);
    const [verOpen, setVerOpen] = useState(false);
    const [chOpen, setChOpen] = useState(false);
    const [q, setQ] = useState('');

    useEffect(() => {
        socialService.config().then(setCfg).catch(() => setCfg(null));
        socialService.accounts().then(setAccounts).catch(() => setAccounts([]));
    }, []);

    // Default to the newest finished render (a fresh 9:16 render lands first).
    useEffect(() => {
        if (!done.length || done.some((r) => r.id === renderId)) return;
        setRenderId(done[0].id);
    }, [done, renderId]);

    const render = done.find((r) => r.id === renderId);
    const versionOf = (r: VideoRender) => `v${renders.length - renders.findIndex((x) => x.id === r.id)}`;
    const vertical = (acc: SocialAccount) => !!cfg?.verticalOnly.includes(acc.platform);
    const feedOk = (acc: SocialAccount) => !vertical(acc) || !render?.format || render.format === 'portrait';
    const storyable = (acc: SocialAccount) => !!cfg?.stories.platforms.includes(acc.platform);
    /** Why this render cannot go to a Story ('' = it can). */
    const storyIssue = (() => {
        if (render?.format && render.format !== 'portrait') return '9:16 only';
        const max = cfg?.stories.maxSeconds;
        if (max && render?.durationSec && render.durationSec > max + 0.5) return `max ${max}s`;
        return '';
    })();
    const placementsFor = (acc: SocialAccount): Placement[] => (['feed', 'story'] as Placement[]).filter((p) => (p === 'feed' ? feedOk(acc) : storyable(acc) && !storyIssue));
    const blocked = (acc: SocialAccount) => (storyable(acc) ? placementsFor(acc).length === 0 : !feedOk(acc));
    const selectable = (acc: SocialAccount) => acc.status === 'ACTIVE' && !blocked(acc);
    const hasPortrait = done.some((r) => r.format === 'portrait');
    const portraitRendering = renders.some((r) => (r.status === 'QUEUED' || r.status === 'RENDERING') && r.format === 'portrait');

    const setOpt = (id: string, patch: PostOptions) => setOptions((o) => ({ ...o, [id]: { ...o[id], ...patch } }));
    const add = (acc: SocialAccount) => {
        setPicked((p) => (p.includes(acc.id) ? p : [...p, acc.id]));
        setOpenAcct((o) => ({ ...o, [acc.id]: true }));
        if (acc.platform === 'youtube' && !options[acc.id]?.title) setOpt(acc.id, { title: videoName, privacy: 'public', madeForKids: false });
        // Reel by default; Story only when the feed is not possible for this render.
        if (storyable(acc)) setOpt(acc.id, { placements: placementsFor(acc).slice(0, 1) });
        if (acc.platform === 'instagram' && options[acc.id]?.shareToFeed === undefined) setOpt(acc.id, { shareToFeed: true });
        // TikTok's rules: read the creator's options right before posting, no default privacy.
        if (acc.platform === 'tiktok' && !creators[acc.id]) {
            socialService.tiktokCreator(acc.id)
                .then((c) => setCreators((m) => ({ ...m, [acc.id]: c })))
                .catch((e) => { setCreators((m) => ({ ...m, [acc.id]: 'error' })); toast.error(vsError(e)); });
        }
    };
    const toggle = (acc: SocialAccount) => {
        if (picked.includes(acc.id)) setPicked((p) => p.filter((x) => x !== acc.id));
        else if (selectable(acc)) add(acc);
    };

    // Render switched (format/length): drop placements and accounts it cannot go to.
    useEffect(() => {
        if (!accounts) return;
        setOptions((o) => Object.fromEntries(Object.entries(o).map(([id, v]) => {
            const a = accounts.find((x) => x.id === id);
            return [id, a && storyable(a) ? { ...v, placements: (v.placements ?? []).filter((p) => placementsFor(a).includes(p)) } : v];
        })));
        setPicked((p) => p.filter((id) => { const a = accounts.find((x) => x.id === id); return a && !blocked(a); }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [renderId, accounts, cfg]);

    const chosen = (accounts || []).filter((a) => picked.includes(a.id));
    const maxAhead = new Date(Date.now() + (cfg?.maxAheadDays ?? 90) * 86400_000);
    /** What blocks step 2 (accounts, options). */
    const channelProblem = (() => {
        if (!render) return 'Render the video first.';
        if (!chosen.length) return 'Pick at least one account.';
        for (const a of chosen) {
            const o = options[a.id] || {};
            if (a.platform === 'youtube' && !o.title?.trim()) return 'Add a YouTube title.';
            if (storyable(a) && !o.placements?.length) return `${a.name}: choose Reel or Story.`;
            if (a.platform === 'tiktok') {
                const c = creators[a.id];
                if (!c) return 'Reading the TikTok account…';
                if (c === 'error') return 'Could not read the TikTok account. Try reconnecting it.';
                if (!o.privacy) return 'Choose who can see the video on TikTok.';
                if (o.brandedContent && o.privacy === 'SELF_ONLY') return 'Paid partnership posts can\'t be "Only me" on TikTok.';
                if (render.durationSec && render.durationSec > c.maxDurationSec + 0.5) return `TikTok: the video is ${Math.round(render.durationSec)}s, this account allows up to ${c.maxDurationSec}s.`;
            }
        }
        return '';
    })();
    const at = joinLocal(day, time);
    const problem = channelProblem || (when === 'later'
        ? (at.getTime() < Date.now() ? 'Pick a time in the future.' : at.getTime() > maxAhead.getTime() ? `You can schedule up to ${cfg?.maxAheadDays ?? 90} days ahead.` : '')
        : '');

    const submit = async () => {
        if (problem || !render || busy) return;
        setBusy(true);
        try {
            await socialService.schedule({
                renderId: render.id,
                accountIds: picked,
                caption,
                options: Object.fromEntries(picked.map((id) => [id, options[id] || {}])),
                scheduledAt: when === 'later' ? at.toISOString() : undefined,
            });
            toast.success(when === 'now' ? 'Posting now. Track it under Posts.' : 'Post scheduled.');
            onScheduled();
            onClose();
        } catch (e) {
            toast.error(vsError(e));
        } finally {
            setBusy(false);
        }
    };

    const fmt = render?.format ?? 'portrait';
    const sideW = fmt === 'landscape' ? 420 : fmt === 'square' ? 360 : 300;
    const nextOk = step === 1 ? !!render : !channelProblem;
    const go = (n: 1 | 2 | 3) => { setVerOpen(false); setChOpen(false); setStep(n); };
    const sec: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 10 };
    const h: React.CSSProperties = { fontSize: 13.5, fontWeight: 700 };

    const portraitNotice = render && render.format && render.format !== 'portrait' && accounts?.some(vertical) ? (
        <div style={{ padding: '10px 12px', borderRadius: 6, background: 'var(--hv-page-2)', fontSize: 12.5, lineHeight: 1.5, color: 'var(--hv-body)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ flex: 1, minWidth: 180 }}>TikTok, Reels and Stories need a vertical 9:16 video.{hasPortrait ? ' Pick a 9:16 version, or render a new one.' : ''}</span>
            <button type="button" className="hv-vs-bd" disabled={portraitRendering} onClick={onRenderPortrait} style={{ height: 30, padding: '0 12px', borderRadius: 999, border: '1px solid var(--hv-line-2)', background: 'var(--hv-card)', cursor: portraitRendering ? 'default' : 'pointer', fontSize: 12.5, fontWeight: 600, color: 'var(--hv-ink)', display: 'flex', alignItems: 'center', gap: 6 }}>
                {portraitRendering ? <><Spin size={11} /> Rendering 9:16…</> : `Render a 9:16 copy · ${renderCost} credits`}
            </button>
        </div>
    ) : null;

    const footer = (
        <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 8, padding: '16px 24px', borderTop: '1px solid var(--hv-line)' }}>
            <div style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: 'var(--hv-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={step > 1 ? problem : ''}>
                {step === 2 && chosen.length > 0 ? channelProblem : step === 3 ? problem : ''}
            </div>
            {step === 1 ? (
                <button type="button" className="hv-vs-ghost" onClick={onClose} style={pillBtn(false)}>Cancel</button>
            ) : (
                <button type="button" className="hv-vs-ghost" onClick={() => go((step - 1) as 1 | 2)} style={pillBtn(false)}>Back</button>
            )}
            {step < 3 ? (
                <button type="button" className={nextOk ? 'hv-vs-deep' : undefined} disabled={!nextOk} onClick={() => go((step + 1) as 2 | 3)} style={pillBtn(true, !nextOk)}>Next</button>
            ) : (
                <button type="button" data-track="schedule_submit" className={problem ? undefined : 'hv-vs-deep'} disabled={!!problem || busy || cfg?.enabled === false} onClick={submit} style={pillBtn(true, !!problem || busy || cfg?.enabled === false)}>
                    {busy && <Spin light />}{when === 'later' ? 'Schedule post' : 'Post'}
                </button>
            )}
        </div>
    );

    const q2 = q.trim().toLowerCase();
    const visible = (accounts || []).filter((a) => !q2 || a.name.toLowerCase().includes(q2) || PLATFORM_NAME[a.platform].toLowerCase().includes(q2));
    const visSel = visible.filter(selectable);
    const nOn = visSel.filter((a) => picked.includes(a.id)).length;
    const anyProvider = cfg && Object.values(cfg.providers).some(Boolean);

    return (
        <Drawer title="Post to social" label="Post to social" width={422 + sideW} onClose={onClose} theme={theme ?? readTheme()} footer={footer}>
            {/* Stepper */}
            <div style={{ flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '14px 24px', borderBottom: '1px solid var(--hv-line)' }}>
                {([[1, 'Choose version', true], [2, 'Channel & caption', !!render], [3, 'Schedule', !channelProblem]] as [1 | 2 | 3, string, boolean][]).map(([n, label, reach]) => {
                    const cur = n === step;
                    const fin = n < step;
                    const dis = !reach || cur;
                    return (
                        <div key={n} style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 'none', minWidth: 0 }}>
                            <button type="button" disabled={dis} aria-current={cur ? 'step' : undefined} onClick={() => go(n)} style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 8, padding: 0, border: 0, background: 'transparent', cursor: dis ? 'default' : 'pointer' }}>
                                <span style={{ flex: 'none', width: 22, height: 22, borderRadius: 999, border: `1.5px solid ${cur || fin ? BRAND : 'var(--hv-line-2)'}`, background: fin ? BRAND : cur ? 'var(--hv-mint)' : 'transparent', color: fin ? '#FFFDF7' : cur ? 'var(--hv-mint-ink)' : 'var(--hv-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11.5, fontWeight: 600 }}>
                                    {fin ? Ico.check(11, 'currentColor', 3.2) : n}
                                </span>
                                <span style={{ whiteSpace: 'nowrap', fontSize: 13, fontWeight: cur ? 600 : 400, color: cur || fin ? 'var(--hv-ink)' : 'var(--hv-muted)' }}>{label}</span>
                            </button>
                            {n < 3 && <span style={{ flex: 'none', width: 40, height: 1, background: 'var(--hv-line-2)' }} />}
                        </div>
                    );
                })}
            </div>

            <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: `minmax(0,420px) minmax(0,${sideW}px)` }}>
                <div style={{ minHeight: 0, overflowY: 'auto', padding: '20px 24px 24px', display: 'flex', flexDirection: 'column' }}>
                    {cfg && !cfg.enabled && <div style={{ marginBottom: 16, fontSize: 13, color: 'var(--hv-danger)' }}>Posting isn't available on this server yet.</div>}

                    {step === 1 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
                            <div style={sec}>
                                <div style={h}>Video</div>
                                {!done.length ? (
                                    <div style={{ fontSize: 13, color: 'var(--hv-muted)', lineHeight: 1.55 }}>No rendered version yet. Click <b style={{ color: 'var(--hv-ink)' }}>Render</b> first.</div>
                                ) : (
                                    <div style={{ position: 'relative' }}>
                                        <button type="button" className="hv-vs-bd" aria-haspopup="listbox" aria-expanded={verOpen} onClick={() => setVerOpen((o) => !o)} style={ddButton(verOpen)}>
                                            <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                <span style={{ fontWeight: 500 }}>{render ? versionOf(render) : 'Choose'}</span>
                                                {render && <span style={{ color: 'var(--hv-muted)' }}> · {versionMeta(render)}</span>}
                                            </span>
                                            {Ico.chevD(14, { color: 'var(--hv-muted)', transform: `rotate(${verOpen ? 180 : 0}deg)`, transition: 'transform 0.2s ease' })}
                                        </button>
                                        {verOpen && (
                                            <>
                                                <div onClick={() => setVerOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 29 }} />
                                                <div role="listbox" aria-label="Video version" style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 31, maxHeight: 280, overflowY: 'auto', padding: 6, borderRadius: 8, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 20px 40px -18px var(--hv-shadow)', animation: 'hvDdIn 0.18s ease both' }}>
                                                    {done.map((r) => (
                                                        <button key={r.id} type="button" role="option" aria-selected={r.id === renderId} className="hv-vs-mi" onClick={() => { setRenderId(r.id); setVerOpen(false); }} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '8px 10px', border: 0, borderRadius: 5, background: 'transparent', color: 'var(--hv-ink)', fontSize: 13, cursor: 'pointer', textAlign: 'left' }}>
                                                            <span style={{ flex: 1, minWidth: 0 }}><span style={{ fontWeight: 500 }}>{versionOf(r)}</span><span style={{ color: 'var(--hv-muted)' }}> · {versionMeta(r)}</span></span>
                                                            {r.id === renderId && Ico.check()}
                                                        </button>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                                {portraitNotice}
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
                            <div style={sec}>
                                <div style={h}>Post to</div>
                                {accounts === null ? <Spin /> : accounts.length === 0 ? (
                                    <div style={{ fontSize: 13, color: 'var(--hv-muted)', lineHeight: 1.55 }}>
                                        No accounts connected yet.{' '}
                                        {anyProvider || !cfg ? <button type="button" className="hv-vs-under" onClick={() => navigate('/dashboard/integration')} style={{ border: 0, background: 'transparent', padding: 0, cursor: 'pointer', fontWeight: 600, color: 'var(--hv-brand-ink)', fontSize: 13 }}>Add channel</button> : 'Integrations aren\'t configured on this server.'}
                                    </div>
                                ) : (
                                    <div style={{ position: 'relative', width: '100%', maxWidth: 360 }}>
                                        <button type="button" className="hv-vs-bd" aria-haspopup="listbox" aria-expanded={chOpen} onClick={() => setChOpen((o) => !o)} style={ddButton(chOpen)}>
                                            <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: picked.length ? 'var(--hv-ink)' : 'var(--hv-muted)' }}>{picked.length ? `${picked.length} selected` : 'Choose accounts'}</span>
                                            {Ico.chevD(14, { color: 'var(--hv-muted)', transform: `rotate(${chOpen ? 180 : 0}deg)`, transition: 'transform 0.2s ease' })}
                                        </button>
                                        {chOpen && (
                                            <>
                                                <div onClick={() => setChOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 29 }} />
                                                <div role="listbox" aria-multiselectable="true" aria-label="Accounts" style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 31, maxHeight: 300, overflowY: 'auto', padding: 6, borderRadius: 8, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 20px 40px -18px var(--hv-shadow)', animation: 'hvDdIn 0.18s ease both' }}>
                                                    <div style={{ position: 'sticky', top: -6, zIndex: 2, margin: '-6px -6px 0', padding: '10px 14px 0', background: 'var(--hv-card)' }}>
                                                        <label className="hv-vs-in" style={{ display: 'flex', alignItems: 'center', gap: 8, height: 34, padding: '0 10px', borderRadius: 6, border: '1px solid var(--hv-line-2)', background: 'var(--hv-card)', color: 'var(--hv-muted)' }}>
                                                            {Ico.search()}
                                                            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search accounts" aria-label="Search accounts" style={{ flex: 1, minWidth: 0, height: '100%', border: 0, outline: 'none', background: 'transparent', color: 'var(--hv-ink)', font: 'inherit', fontSize: 13 }} />
                                                        </label>
                                                        {visSel.length > 1 && (
                                                            <button type="button" role="option" aria-selected={nOn === visSel.length} className="hv-vs-mi" onClick={() => { if (nOn === visSel.length) setPicked((p) => p.filter((id) => !visSel.some((a) => a.id === id))); else visSel.forEach(add); }} style={{ display: 'flex', alignItems: 'center', gap: 10, width: 'calc(100% + 16px)', height: 36, margin: '4px -8px 2px', padding: '0 8px', border: 0, borderRadius: 5, background: 'transparent', color: 'var(--hv-ink)', fontSize: 13, cursor: 'pointer', textAlign: 'left' }}>
                                                                <CheckBox on={nOn === visSel.length} partial={nOn > 0} />
                                                                <span style={{ flex: 1 }}>Select all</span>
                                                            </button>
                                                        )}
                                                        {!visible.length && <div style={{ padding: '18px 8px 14px', fontSize: 12.5, color: 'var(--hv-muted)', textAlign: 'center' }}>No accounts match “{q}”.</div>}
                                                    </div>
                                                    {visible.map((a) => {
                                                        const on = picked.includes(a.id);
                                                        const ok = selectable(a);
                                                        const note = a.status !== 'ACTIVE' ? ' · Reconnect' : blocked(a) ? ' · 9:16 only' : '';
                                                        return (
                                                            <button key={a.id} type="button" role="option" aria-selected={on} disabled={!ok && !on} className="hv-vs-mi" onClick={() => toggle(a)} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: 8, border: 0, borderRadius: 5, background: 'transparent', color: 'var(--hv-ink)', cursor: ok || on ? 'pointer' : 'not-allowed', opacity: ok || on ? 1 : 0.5, textAlign: 'left' }}>
                                                                <CheckBox on={on} />
                                                                <PlatformIcon platform={a.platform} />
                                                                <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                                    <span style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 13 }}>{a.name}</span>
                                                                    <span style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 11.5, color: a.status !== 'ACTIVE' ? 'var(--hv-danger)' : 'var(--hv-body)' }}>{PLATFORM_NAME[a.platform]}{note}</span>
                                                                </span>
                                                            </button>
                                                        );
                                                    })}
                                                    <div style={{ height: 1, margin: '4px 6px', background: 'var(--hv-line)' }} />
                                                    <button type="button" className="hv-vs-mi" onClick={() => navigate('/dashboard/integration')} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '8px 10px', border: 0, borderRadius: 5, background: 'transparent', color: 'var(--hv-ink)', fontSize: 13, cursor: 'pointer', textAlign: 'left' }}>
                                                        <span style={{ flex: 'none', width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--hv-muted)' }}>{Ico.plus(14)}</span>
                                                        <span>Add channel</span>
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                                {portraitNotice}
                            </div>

                            {chosen.length > 0 && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: -16 }}>
                                    {chosen.map((a) => {
                                        const open = !!openAcct[a.id];
                                        return (
                                            <div key={a.id} style={{ border: '1px solid var(--hv-line)', borderRadius: 8, background: 'var(--hv-card)' }}>
                                                <button type="button" aria-expanded={open} onClick={() => setOpenAcct((o) => ({ ...o, [a.id]: !open }))} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '12px 16px', border: 0, borderRadius: 8, background: 'transparent', color: 'var(--hv-ink)', cursor: 'pointer', textAlign: 'left' }}>
                                                    {Ico.chevR(14, { color: 'var(--hv-muted)', transform: `rotate(${open ? 90 : 0}deg)`, transition: 'transform 0.2s ease' })}
                                                    <PlatformIcon platform={a.platform} />
                                                    <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 13 }}>{a.name}</span>
                                                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 11.5, color: 'var(--hv-body)' }}>{summaryOf(a, options[a.id] || {}, render?.format === 'portrait')}</span>
                                                    </span>
                                                </button>
                                                {open && (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, padding: '16px 16px 18px', borderTop: '1px solid var(--hv-line)' }}>
                                                        {storyable(a) && (
                                                            <PlacementFields
                                                                account={a}
                                                                value={options[a.id] || {}}
                                                                feedIssue={feedOk(a) ? '' : '9:16 only'}
                                                                storyIssue={storyIssue}
                                                                portrait={render?.format === 'portrait'}
                                                                onChange={(p) => setOpt(a.id, p)}
                                                            />
                                                        )}
                                                        <PlatformFields account={a} value={options[a.id] || {}} onChange={(p) => setOpt(a.id, p)} creator={creators[a.id]} />
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                                    <div style={{ flex: 1, ...h }}>Caption</div>
                                    <span style={{ fontSize: 12, color: 'var(--hv-muted)', fontVariantNumeric: 'tabular-nums' }}>{caption.length}/{CAPTION_MAX}</span>
                                </div>
                                <textarea className="hv-vs-in" value={caption} onChange={(e) => setCaption(e.target.value)} rows={4} maxLength={CAPTION_MAX} placeholder="Write what goes with the video…" style={{ ...textareaStyle, minHeight: 96, padding: '11px 12px', fontSize: 13.5, lineHeight: 1.55 }} />
                                {chosen.some((a) => a.platform === 'threads') && cfg?.maxText.threads ? <div style={{ fontSize: 11.5, color: 'var(--hv-muted)' }}>Threads uses the first {cfg.maxText.threads} characters.</div> : null}
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
                            <div style={sec}>
                                <div style={h}>When</div>
                                <div role="radiogroup" aria-label="When" style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
                                    <Radio size={18} on={when === 'now'} onPick={() => setWhen('now')}><span style={{ fontSize: 13.5, height: 32, display: 'flex', alignItems: 'center' }}>Post now</span></Radio>
                                    <Radio size={18} on={when === 'later'} onPick={() => setWhen('later')}><span style={{ fontSize: 13.5, height: 32, display: 'flex', alignItems: 'center' }}>Schedule</span></Radio>
                                </div>
                                {when === 'later' && (
                                    <>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr)', gap: 10 }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                                                <span style={fieldLabel}>Date</span>
                                                <DateField value={day} max={maxAhead} onChange={(d) => { setDay(d); if (joinLocal(d, time).getTime() < Date.now()) setTime(hhmm(nextHour())); }} />
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                                                <span style={fieldLabel}>Time</span>
                                                <TimeField day={day} value={time} onChange={setTime} />
                                            </div>
                                        </div>
                                        <div style={{ fontSize: 11.5, color: 'var(--hv-muted)' }}>{TZ_NAME}</div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div style={{ minHeight: 0, overflowY: 'auto', padding: 20, borderLeft: '1px solid var(--hv-line)', background: 'var(--hv-page-2)', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 14 }}>
                    {render?.url ? (
                        <RenderPreview key={render.id} url={render.url} format={fmt} />
                    ) : (
                        <div style={{ alignSelf: 'center', width: '100%', maxWidth: fmt === 'portrait' ? 190 : '100%', aspectRatio: fmt === 'portrait' ? '9 / 16' : fmt === 'square' ? '1 / 1' : '16 / 9', borderRadius: 8, background: 'var(--hv-sand)' }} />
                    )}
                </div>
            </div>
        </Drawer>
    );
};

function nextHour() {
    const d = new Date(Date.now() + 60 * 60_000);
    d.setMinutes(0, 0, 0);
    return d;
}

const versionMeta = (r: VideoRender) => [aspectLabel(r.format) || '?', r.durationSec ? fmtT(r.durationSec) : '', fmtMB(r.bytes)].filter(Boolean).join(' · ');

function summaryOf(a: SocialAccount, o: PostOptions, portrait: boolean): string {
    const P = PLATFORM_NAME[a.platform];
    if (a.platform === 'tiktok') return `${P} · ${o.privacy ? TIKTOK_PRIVACY[o.privacy] ?? o.privacy : 'Choose who can see this'}`;
    if (a.platform === 'youtube') return `${P}${portrait ? ' Shorts' : ''} · ${{ public: 'Public', unlisted: 'Unlisted', private: 'Private' }[o.privacy ?? 'public'] ?? o.privacy}`;
    if (a.platform === 'instagram' || a.platform === 'facebook') {
        const feed = a.platform === 'facebook' && !portrait ? 'Page video' : 'Reel';
        const parts = (o.placements ?? []).map((p) => (p === 'feed' ? feed : 'Story'));
        return `${P} · ${parts.join(' + ') || 'No format'}`;
    }
    return P;
}

/** Plays the exact file that will be posted (TikTok's Direct Post rules ask for a preview). */
const RenderPreview: React.FC<{ url: string; format: string }> = ({ url, format }) => {
    const ref = useRef<HTMLVideoElement>(null);
    const [playing, setPlaying] = useState(false);
    const [t, setT] = useState(0);
    const [dur, setDur] = useState(0);
    const toggle = () => {
        const v = ref.current;
        if (!v) return;
        if (v.paused) v.play().catch(() => {}); else v.pause();
    };
    return (
        <div style={{ position: 'relative', alignSelf: 'center', width: '100%', maxWidth: format === 'portrait' ? 190 : '100%', aspectRatio: format === 'portrait' ? '9 / 16' : format === 'square' ? '1 / 1' : '16 / 9', borderRadius: 8, overflow: 'hidden', background: '#0E1110' }}>
            <video
                ref={ref}
                src={url}
                playsInline
                preload="metadata"
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => setPlaying(false)}
                onTimeUpdate={(e) => setT(e.currentTarget.currentTime)}
                onLoadedMetadata={(e) => setDur(e.currentTarget.duration || 0)}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
            <button type="button" onClick={toggle} aria-label={playing ? 'Pause preview' : 'Play preview'} style={{ position: 'absolute', inset: 0, border: 0, padding: 0, background: playing ? 'transparent' : 'rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#FFFDF7' }}>
                <span style={{ width: 44, height: 44, borderRadius: 999, background: 'rgba(20,22,24,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: playing ? 0 : 1, transition: 'opacity 0.2s ease' }}>
                    {playing ? Ico.pause(16) : Ico.play(16)}
                </span>
            </button>
            <div style={{ position: 'absolute', left: 10, right: 10, bottom: 10, display: 'flex', alignItems: 'center', gap: 8, color: '#FFFDF7', fontSize: 11, fontVariantNumeric: 'tabular-nums', pointerEvents: 'none' }}>
                <span>{fmtT(t)}</span>
                <span style={{ flex: 1, height: 3, borderRadius: 999, background: 'rgba(255,253,247,0.35)', overflow: 'hidden' }}>
                    <span style={{ display: 'block', height: '100%', width: `${dur ? (t / dur) * 100 : 0}%`, background: '#FFFDF7' }} />
                </span>
                <span>{fmtT(dur)}</span>
            </div>
        </div>
    );
};

const subLabel: React.CSSProperties = { fontSize: 12, color: 'var(--hv-body)' };
const group = (label: string, children: React.ReactNode, row?: boolean) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <span style={subLabel}>{label}</span>
        <div style={{ display: 'flex', flexDirection: row ? 'row' : 'column', flexWrap: row ? 'wrap' : undefined, gap: row ? '8px 18px' : 10 }}>{children}</div>
    </div>
);

/** Facebook / Instagram: feed (Reel / Page video) and/or Story; each becomes its own post. */
const PlacementFields: React.FC<{ account: SocialAccount; value: PostOptions; feedIssue: string; storyIssue: string; portrait: boolean; onChange: (p: PostOptions) => void }> = ({ account, value, feedIssue, storyIssue, portrait, onChange }) => {
    const list = value.placements ?? [];
    const feedLabel = account.platform === 'facebook' && !portrait ? 'Page video' : 'Reel';
    const item = (p: Placement, text: string, issue: string) => (
        <Check on={!issue && list.includes(p)} disabled={!!issue} onChange={(on) => onChange({ placements: on ? [...list.filter((x) => x !== p), p] : list.filter((x) => x !== p) })}>
            {text}{issue ? ` (${issue})` : ''}
        </Check>
    );
    return (
        <>
            {group('Format', <>{item('feed', feedLabel, feedIssue)}{item('story', 'Story', storyIssue)}</>)}
            {list.includes('story') && <div style={{ marginTop: -8, fontSize: 12, lineHeight: 1.5, color: 'var(--hv-muted)' }}>Stories don't show the caption. Reel and Story go out as two separate posts.</div>}
            {account.platform === 'instagram' && list.includes('feed') && group('Options', (
                <Check on={value.shareToFeed !== false} onChange={(on) => onChange({ shareToFeed: on })}>Also show in Feed</Check>
            ))}
        </>
    );
};

/** Per-platform fields under a picked account. */
const PlatformFields: React.FC<{ account: SocialAccount; value: PostOptions; onChange: (p: PostOptions) => void; creator?: TiktokCreator | 'error' }> = ({ account, value, onChange, creator }) => {
    const note: React.CSSProperties = { fontSize: 12, lineHeight: 1.55, color: 'var(--hv-body)' };
    const link = (href: string, text: string) => <a href={href} target="_blank" rel="noreferrer" className="hv-vs-under" style={{ color: 'var(--hv-brand-ink)' }}>{text}</a>;
    if (account.platform === 'youtube') {
        return (
            <>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <span style={subLabel}>Title</span>
                    <input className="hv-vs-in" value={value.title ?? ''} maxLength={100} onChange={(e) => onChange({ title: e.target.value })} style={{ ...inputStyle, padding: '0 12px', fontSize: 13.5 }} />
                </label>
                {group('Visibility', (['public', 'unlisted', 'private'] as const).map((v) => (
                    <Radio key={v} on={(value.privacy ?? 'public') === v} onPick={() => onChange({ privacy: v })}>{{ public: 'Public', unlisted: 'Unlisted', private: 'Private' }[v]}</Radio>
                )), true)}
                {group('Made for kids?', <>
                    <Radio on={value.madeForKids !== true} onPick={() => onChange({ madeForKids: false })}>No</Radio>
                    <Radio on={value.madeForKids === true} onPick={() => onChange({ madeForKids: true })}>Yes</Radio>
                </>, true)}
                <div style={note}>9:16 videos show up as Shorts.</div>
                <div style={{ ...note, fontSize: 11.5, color: 'var(--hv-muted)' }}>
                    HyVay uses YouTube API Services. By posting to YouTube you agree to the {link('https://www.youtube.com/t/terms', 'YouTube Terms of Service')}, the {link('/privacy', 'HyVay Privacy Policy')} and the {link('https://policies.google.com/privacy', 'Google Privacy Policy')}. You can revoke access any time from HyVay or your {link('https://security.google.com/settings/security/permissions', 'Google security settings')}.
                </div>
            </>
        );
    }
    if (account.platform === 'tiktok') {
        if (!creator) return <div style={{ display: 'flex', alignItems: 'center', gap: 8, ...note }}><Spin /> Reading the TikTok account…</div>;
        if (creator === 'error') return <div style={{ fontSize: 12.5, color: 'var(--hv-danger)' }}>Could not read the TikTok account. Try reconnecting it.</div>;
        const opts = TIKTOK_ORDER.filter((p) => creator.privacyOptions.includes(p) || !creator.audited)
            .concat(creator.privacyOptions.filter((p) => !TIKTOK_ORDER.includes(p)));
        const allow = (key: 'allowComment' | 'allowDuet' | 'allowStitch', text: string, off: boolean) => (
            <Check key={key} on={!off && value[key] !== false} disabled={off} onChange={(on) => onChange({ [key]: on })}>{text}{off ? ' (turned off in TikTok)' : ''}</Check>
        );
        return (
            <>
                {!creator.audited && (
                    <div style={{ padding: '10px 12px', borderRadius: 6, background: 'var(--hv-sand)', color: 'var(--hv-sand-ink)', fontSize: 12.5, lineHeight: 1.55 }}>
                        Until TikTok approves our app, posts are <strong style={{ fontWeight: 600 }}>Only me</strong> and your account must be private.
                    </div>
                )}
                <div style={note}>Posting as <b style={{ color: 'var(--hv-ink)', fontWeight: 600 }}>{creator.nickname}</b> · up to {creator.maxDurationSec}s</div>
                {group('Who can see this', opts.map((p) => (
                    <Radio key={p} on={value.privacy === p} disabled={!creator.privacyOptions.includes(p)} onPick={() => onChange({ privacy: p })}>{TIKTOK_PRIVACY[p] ?? p}</Radio>
                )), true)}
                {group('Allow', <>
                    {allow('allowComment', 'Comment', creator.commentDisabled)}
                    {allow('allowDuet', 'Duet', creator.duetDisabled)}
                    {allow('allowStitch', 'Stitch', creator.stitchDisabled)}
                </>)}
                {group('Disclosure', <>
                    <Check on={!!value.brandOrganic} onChange={(on) => onChange({ brandOrganic: on })}>Promotional content</Check>
                    <Check on={!!value.brandedContent} onChange={(on) => onChange({ brandedContent: on })}>Paid partnership</Check>
                </>)}
                <div style={note}>
                    Agrees to TikTok {link('https://www.tiktok.com/legal/page/global/music-usage-confirmation/en', 'Music Usage Confirmation')}
                    {value.brandedContent && <> and {link('https://www.tiktok.com/legal/page/global/bc-policy/en', 'Branded Content Policy')}</>}.
                </div>
            </>
        );
    }
    if (account.platform === 'linkedin' || account.platform === 'facebook') {
        return (
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={subLabel}>Video title (optional)</span>
                <input className="hv-vs-in" value={value.title ?? ''} maxLength={200} onChange={(e) => onChange({ title: e.target.value })} style={{ ...inputStyle, padding: '0 12px', fontSize: 13.5 }} />
            </label>
        );
    }
    if (account.platform === 'threads') return <div style={note}>Posted as a video on your Threads profile, with the caption as its text.</div>;
    return null;
};
